import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'
import VariantModal from './VariantModal'
import PaymentModal from './PaymentModal'
import SaveCustomerModal from './SaveCustomerModal'
import { formatCurrency, formatLempiras } from '../../lib/format'
import { usePosCartChannel, type CartItem } from '../../hooks/usePosCartChannel'
import { useFocusRefetch } from '../../hooks/useFocusRefetch'

// ── Tipos ──────────────────────────────────────────

interface ProductImage {
  id: string
  url: string
  filename: string
  content_type?: string
  byte_size?: number
}

interface Variant {
  id: number
  sku: string | null
  price: number | null
  stock_quantity: number
  option_display: string
}

interface Product {
  id: number
  name: string
  sku: string | null
  price: number
  tax_rate: number
  track_inventory: boolean
  track_variants: boolean
  stock_quantity: number
  category_name: string | null
  product_images: ProductImage[]
  variants: Variant[]
}

interface Customer {
  id: number
  name: string
  phone?: string
  email?: string
}

interface Category {
  id: number
  name: string
}

interface Account {
  id: number
  name: string
  slug: string
  tenant_type: string
}

interface Props {
  products: Product[]
  categories: Category[]
  customers: Customer[]
  account: Account
  can_create_customers: boolean
  show_save_customer_prompt?: boolean
  permissions?: Record<string, unknown>
  /** Carrito persistido en el backend (rehidratado en cada carga) */
  cart: CartItem[]
  pagination?: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

// Tamaño de página fijo y COMPARTIDO con el backend (100 artículos por
// categoría, spec 2026-07-21). El grid NUNCA salta de página solo: cada
// mutación envía la página actual y el servidor la respeta.
const POS_PER_PAGE = 100

// ── Componente ─────────────────────────────────────

export default function POSIndex({
  products: initialProducts,
  categories,
  customers: initialCustomers,
  account,
  can_create_customers,
  show_save_customer_prompt = false,
  permissions,
  cart: initialCart,
  pagination,
}: Props) {
  // ── Estado del carrito (viene del backend, siempre rehidratado) ──
  const [cart, setCart] = useState<CartItem[]>(initialCart)
  const [search, setSearch] = useState('')
  const [selectedCategoryId, setSelectedCategoryId] = useState('')

  // ── Estado de paginación ──
  const [products, setProducts] = useState<Product[]>(initialProducts)
  const [currentPage, setCurrentPage] = useState(1)

  // ── Sincronizar productos cuando el backend re-renderiza (mutaciones,
  //    búsqueda, cambio de categoría, paginación). Reemplaza (no acumula):
  //    el paginador estándar muestra la página actual completa. ──
  useEffect(() => {
    setProducts(initialProducts)
    setCurrentPage(pagination?.page || 1)
  }, [initialProducts, pagination])

  // ── Estado de modales ──
  const [showVariantModal, setShowVariantModal] = useState(false)
  const [variantProduct, setVariantProduct] = useState<Product | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showSaveCustomerModal, setShowSaveCustomerModal] = useState(show_save_customer_prompt)

  // ── Estado de pago ──
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [selectedCustomer, setSelectedCustomer] = useState('')
  const [customers, setCustomers] = useState<Customer[]>(initialCustomers)
  // product_id (string) → seriales por unidad. Backend lee params[:serial_numbers][id.to_s]
  const [serialNumbers, setSerialNumbers] = useState<Record<string, string[]>>({})

  // ── Estados nuevos: B3 cambio, B4 comentarios, B6 tipo de consumo ──
  const [amountReceived, setAmountReceived] = useState('')
  const [orderNotes, setOrderNotes] = useState('')
  const [consumptionType, setConsumptionType] = useState('dine_in')

  // ── Processing state ──
  const [processing, setProcessing] = useState(false)
  const [cartError, setCartError] = useState<string | null>(null)

  // ── Refs ──
  const cartRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Cart visibility (for sticky button) ──
  const [cartInView, setCartInView] = useState(false)
  useEffect(() => {
    const el = cartRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setCartInView(entry.isIntersecting),
      { threshold: 0.3 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // ── Refetch helper (for useFocusRefetch) ──
  // Refetches ONLY the cart+products props without touching the URL.
  // This avoids Inertia version-mismatch reloads (which can trap the browser
  // in a loop if the URL happens to be a 404).
  const refetchPos = useCallback(() => {
    // Refetch EXPLÍCITO a /:slug/pos (GET que sí existe y pagina).
    // Usamos router.get con URL explícita en vez de router.reload(),
    // porque router.reload() usa la URL actual del navegador — y si
    // alguna mutación cambió la URL a /pos/draft_items, el reload haría
    // GET a una ruta que solo acepta POST/PUT/DELETE → 404 en bucle.
    router.get(
      `/${account.slug}/pos`,
      { search, category_id: selectedCategoryId, page: currentPage, per_page: POS_PER_PAGE },
      {
        only: ['cart', 'products', 'pagination'],
        preserveState: true,
        preserveScroll: true,
        replace: true
      }
    )
  }, [currentPage, account.slug, search, selectedCategoryId])

  useFocusRefetch(refetchPos, { debounceMs: 400 })

  // ── WebSocket: subscribe a cambios del carrito en tiempo real ──
  // We do NOT call refetchPos on reconnect — the cable's reconnect
  // events can fire repeatedly on flaky connections, and each refetch
  // is an Inertia request that could trigger a version-mismatch reload.
  // The broadcast itself already updates local state, so we don't need
  // an extra refetch.
  usePosCartChannel({
    onMessage: (payload) => {
      setCart(payload.cart)
    }
  })

  // ── Sincronizar cart con prop inicial cuando Inertia recarga ──
  // (router.reload({ only: [...] }) trae props nuevos sin desmontar el componente)
  useEffect(() => {
    setCart(initialCart)
  }, [initialCart])

  // ── Cálculos del carrito (derivados) ──
  // El descuento de promociones viene del backend (discount_cents por ítem);
  // el ISV se recalcula proporcional sobre el subtotal rebajado.
  const { subtotal, discount, tax, total } = useMemo(() => {
    let sub = 0
    let disc = 0
    let t = 0
    for (const item of cart) {
      sub += item.line_subtotal
      const d = (item.discount_cents ?? 0) / 100
      disc += d
      const discountedSub = item.line_subtotal - d
      t += discountedSub > 0 && item.line_subtotal > 0
        ? item.line_tax * (discountedSub / item.line_subtotal)
        : 0
    }
    return { subtotal: sub, discount: disc, tax: t, total: sub - disc + t }
  }, [cart])

  // ── Handlers: carrito via backend ──

  const handleProductClick = (product: Product) => {
    if (product.track_variants && product.variants.length > 0) {
      setVariantProduct(product)
      setShowVariantModal(true)
    } else {
      addToCart(product, null, 1)
    }
  }

  const addToCart = (product: Product, variant: Variant | null, quantity: number) => {
    setCartError(null)
    router.post(
      `/${account.slug}/pos/draft_items`,
      {
        product_id: product.id,
        variant_id: variant?.id || null,
        quantity,
        // Página actual: el backend la respeta (no salta a 1 solo).
        page: currentPage,
        per_page: POS_PER_PAGE,
        // CRÍTICO: enviar la categoría activa para que el re-render del
        // backend mantenga el mismo subset filtrado (si no, render_pos_index
        // recibe category_id nil y muestra TODOS los productos).
        category_id: selectedCategoryId,
      },
      {
        preserveScroll: true,
        preserveUrl: true,
        onSuccess: (page) => {
          // El backend devuelve cart nuevo en page.props.cart (Inertia re-render).
          // El state local también se actualiza via useEffect([initialCart]).
          const newCart = (page.props as { cart?: CartItem[] }).cart
          if (newCart) setCart(newCart)
          const flash = (page.props as { flash?: { error?: string; notice?: string } }).flash
          if (flash?.error) {
            setCartError(flash.error)
            setTimeout(() => setCartError(null), 4000)
          }
        },
        onError: (errors) => {
          // Inertia pone errors como objeto {field: [msg]} o un string del backend.
          const msg = typeof errors === 'string'
            ? errors
            : Object.values(errors).flat().join(', ') || 'Error al añadir al carrito'
          setCartError(msg)
          setTimeout(() => setCartError(null), 4000)
        },
      }
    )
  }

  const updateQuantity = (draftItemId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(draftItemId)
      return
    }
    setCartError(null)
    router.patch(
      `/${account.slug}/pos/draft_items/${draftItemId}`,
      {
        quantity,
        // Página actual: el backend la respeta (no salta a 1 solo).
        page: currentPage,
        per_page: POS_PER_PAGE,
        // Mantiene el subset filtrado por categoría en el re-render.
        category_id: selectedCategoryId,
      },
      {
        preserveScroll: true,
        preserveUrl: true,
        onSuccess: (page) => {
          const newCart = (page.props as { cart?: CartItem[] }).cart
          if (newCart) setCart(newCart)
        },
        onError: (errors) => {
          const msg = typeof errors === 'string'
            ? errors
            : Object.values(errors).flat().join(', ') || 'Error al actualizar cantidad'
          setCartError(msg)
          setTimeout(() => setCartError(null), 4000)
        },
      }
    )
  }

  const removeFromCart = (draftItemId: number) => {
    setCartError(null)
    router.delete(`/${account.slug}/pos/draft_items/${draftItemId}`, {
      // Página actual: el backend la respeta (no salta a 1 solo).
      data: { page: currentPage, per_page: POS_PER_PAGE, category_id: selectedCategoryId },
      preserveScroll: true,
      preserveUrl: true,
      onSuccess: (page) => {
        const newCart = (page.props as { cart?: CartItem[] }).cart
        setCart(Array.isArray(newCart) ? newCart : [])
      },
      onError: () => {
        setCartError('Error al eliminar del carrito')
        setTimeout(() => setCartError(null), 4000)
      },
    })
  }

  const clearCart = () => {
    if (!confirm('¿Vaciar el carrito?')) return
    setCartError(null)
    router.delete(`/${account.slug}/pos/draft_items/clear`, {
      // Página actual: el backend la respeta (no salta a 1 solo).
      data: { page: currentPage, per_page: POS_PER_PAGE, category_id: selectedCategoryId },
      preserveScroll: true,
      preserveUrl: true,
      onSuccess: (page) => {
        const newCart = (page.props as { cart?: CartItem[] }).cart
        setCart(Array.isArray(newCart) ? newCart : [])
        const flash = (page.props as { flash?: { notice?: string } }).flash
        if (flash?.notice) {
          setCartError(flash.notice)
          setTimeout(() => setCartError(null), 3000)
        }
      },
      onError: () => {
        setCartError('Error al vaciar el carrito. Intenta de nuevo.')
        setTimeout(() => setCartError(null), 4000)
      },
    })
  }

  // ── Filtrado y orden ──
  // El backend ya filtra por categoría (category_id) y búsqueda (search).
  // Aquí solo ordenamos por nombre para una vista estable. El filtro
  // client-side previo por category_name ya no aplica (el server lo hace).
  const visibleProducts = useMemo(() => {
    return [...products].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))
  }, [products])

  // ── Handlers de UI ──

  const handleOpenPayment = () => {
    if (cart.length === 0) return
    setShowPaymentModal(true)
  }

  const handleSerialNumberChange = (productId: number, value: string[]) => {
    setSerialNumbers((prev) => ({ ...prev, [String(productId)]: value }))
  }

  const handleSubmitSale = () => {
    if (cart.length === 0 || processing) return

    // Validar seriales antes de mandar (backend también valida, pero mejor UX acá)
    for (const item of cart) {
      if (!item.product?.requires_serial) continue
      const serials = (serialNumbers[String(item.product_id)] || [])
        .map((s) => s.trim())
        .filter(Boolean)
      if (serials.length !== item.quantity) {
        alert(
          `Seriales para ${item.product.name}: se requieren ${item.quantity}, se ingresaron ${serials.length}`
        )
        return
      }
    }

    // Validar efectivo recibido si el pago es en efectivo
    if (paymentMethod === 'cash') {
      const received = amountReceived ? parseFloat(amountReceived.replace(',', '.')) : 0
      if (!received || received < total) {
        alert(
          `El efectivo recibido debe ser igual o mayor al total a cobrar (${formatCurrency(total)}).`
        )
        return
      }
    }

    // El backend lee el carrito del PosDraftItem del usuario.
    // No enviamos items[] — eso es vulnerable a manipulación.
    setProcessing(true)
    router.post(
      `/${account.slug}/pos/create_sale`,
      {
        customer_id: selectedCustomer || null,
        payment_method: paymentMethod,
        serial_numbers: serialNumbers,
        amount_received_cents: amountReceived ? Math.round(parseFloat(amountReceived.replace(',', '.')) * 100) : null,
        notes: orderNotes || null,
        consumption_type: consumptionType,
      },
      {
        onSuccess: () => {
          setProcessing(false)
          setShowPaymentModal(false)
          // El backend ya vació el draft y emitió broadcast → el cart se actualiza
          setSelectedCustomer('')
          setPaymentMethod('cash')
          setSerialNumbers({})
          setAmountReceived('')
          setOrderNotes('')
          setConsumptionType('dine_in')
        },
        onError: (errors) => {
          setProcessing(false)
          const msg = typeof errors === 'string' ? errors : (Object.values(errors)[0] as string) || 'Error al procesar la venta'
          alert(msg)
        },
      }
    )
  }

  const handleCustomerCreated = (newCustomer: Customer) => {
    setCustomers((prev) => [...prev, newCustomer])
    setSelectedCustomer(String(newCustomer.id))
  }

  // ── Render ──

  return (
    <Layout account={account} permissions={permissions} fullWidth>
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-[#202020]">Punto de Venta</h1>
        <span className="text-sm text-gray-500">{cart.length} items</span>
      </div>

      {/* ── Cart error banner (top) ── */}
      {cartError && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
          <span>{cartError}</span>
          <button
            onClick={() => setCartError(null)}
            className="text-red-700 hover:text-red-900"
            aria-label="Cerrar"
          >×</button>
        </div>
      )}

      {/* ── Grid principal ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* ── Panel izquierdo: productos ── */}
        <div className="xl:col-span-2">
          {/* Búsqueda + filtro */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              type="text"
              placeholder="Buscar producto..."
              value={search}
              onChange={(e) => {
                const v = e.target.value
                setSearch(v)
                // Nueva búsqueda → volvemos a la página 1 de forma deterministica.
                setCurrentPage(1)
                // Enviar búsqueda al servidor con debounce
                if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
                searchTimeoutRef.current = setTimeout(() => {
                  router.get(`/${account.slug}/pos`, { search: v, category_id: selectedCategoryId, page: 1, per_page: POS_PER_PAGE }, { preserveState: true, replace: true })
                }, 300)
              }}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55ACEE] text-base"
            />
          </div>

          {/* Barra de categorías: combobox (más rápido que chips, request 2026-07).
              Búsqueda arriba, selector de categoría abajo. POS abre sin productos;
              el usuario elige categoría o busca. */}
          <select
            value={selectedCategoryId}
            onChange={(e) => {
              const v = e.target.value
              setSelectedCategoryId(v)
              setCurrentPage(1)
              // "Todas" (vacio) sin búsqueda → POS vacío.
              if (v === '' && search.trim() === '') {
                router.get(`/${account.slug}/pos`, { page: 1, per_page: POS_PER_PAGE }, { preserveState: true, replace: true })
              } else {
                router.get(`/${account.slug}/pos`, { search, category_id: v, page: 1, per_page: POS_PER_PAGE }, { preserveState: true, replace: true })
              }
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55ACEE] text-base bg-white"
          >
            <option value="">Todas las categorías</option>
            {categories.map((cat) => (
              <option key={cat.id} value={String(cat.id)}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* Grid de productos */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto">
            {visibleProducts.length === 0 ? (
              <p className="col-span-full text-center text-gray-400 py-12">
                {search.trim() !== '' || selectedCategoryId !== ''
                  ? 'No se encontraron productos'
                  : 'Selecciona una categoría o busca un producto'}
              </p>
            ) : (
              visibleProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onClick={() => handleProductClick(product)}
                />
              ))
            )}
          </div>
          {/* Paginador estándar (flechas + números), spec 2026-07-21 */}
          {pagination && pagination.total_pages > 1 && (
            <PosPagination
              page={pagination.page}
              totalPages={pagination.total_pages}
              onPageChange={(nextPage) => {
                router.get(
                  `/${account.slug}/pos`,
                  { search, category_id: selectedCategoryId, page: nextPage, per_page: POS_PER_PAGE },
                  { preserveState: true, replace: true }
                )
              }}
            />
          )}
        </div>

        {/* ── Panel derecho: carrito ── */}
        <div ref={cartRef} className="bg-white p-4 rounded-lg shadow flex flex-col max-h-[70vh]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-[#202020]">Carrito</h2>
            {cart.length > 0 && (
              <button
                onClick={clearCart}
                className="text-xs text-red-500 hover:text-red-700"
              >
                Vaciar
              </button>
            )}
          </div>

          {cart.length === 0 ? (
            <p className="text-gray-400 text-center py-12 flex-1 flex items-center justify-center">
              Carrito vacío
            </p>
          ) : (
            <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-2">
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#202020] text-sm truncate">
                      {item.display_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(item.unit_price)} c/u
                    </p>
                    {(item.promotions ?? []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.promotions.map((p, i) => (
                          <span
                            key={i}
                            className="inline-block bg-green-100 text-green-700 text-[10px] font-medium px-1.5 py-0.5 rounded"
                          >
                            🏷️ {p.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {(item.discount_cents ?? 0) > 0 && (
                      <p className="text-[11px] text-green-600 font-medium mt-0.5">
                        Ahorro: −{formatLempiras(item.discount_cents)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-7 h-7 rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 flex items-center justify-center"
                      aria-label="Disminuir"
                    >−</button>
                    <span className="w-6 text-center text-sm font-medium tabular-nums">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-7 h-7 rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 flex items-center justify-center"
                      aria-label="Aumentar"
                    >+</button>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="ml-1 w-7 h-7 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center"
                      aria-label="Eliminar"
                    >×</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Totales */}
          {cart.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span className="tabular-nums">{formatCurrency(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-green-600 font-medium">
                  <span>Descuento promociones</span>
                  <span className="tabular-nums">−{formatLempiras(Math.round(discount * 100))}</span>
                </div>
              )}
              {tax > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>ISV</span>
                  <span className="tabular-nums">{formatCurrency(tax)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-base text-[#202020] pt-1">
                <span>Total</span>
                <span className="tabular-nums">{formatCurrency(total)}</span>
              </div>
              <button
                onClick={handleOpenPayment}
                className="w-full mt-2 bg-[#FFC107] text-[#202020] py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors"
              >
                Cobrar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Sticky floating cart button (mobile) ── */}
      {cart.length > 0 && !showPaymentModal && !cartInView && (
        <button
          onClick={() => cartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="lg:hidden fixed bottom-4 left-4 right-4 z-40 bg-[#FFC107] text-[#202020] py-4 px-6 rounded-xl shadow-xl font-bold text-lg flex items-center justify-between"
        >
          <span className="bg-[#202020] text-[#FFC107] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
            {cart.length}
          </span>
          <span className="tabular-nums">{formatCurrency(total)}</span>
          <span className="text-sm opacity-70">Ver carrito ▾</span>
        </button>
      )}

      {/* ── Modales ── */}
      {showVariantModal && variantProduct && (
        <VariantModal
          product={variantProduct}
          onAdd={(variant, qty) => {
            addToCart(variantProduct, variant, qty)
            setShowVariantModal(false)
            setVariantProduct(null)
          }}
          onClose={() => {
            setShowVariantModal(false)
            setVariantProduct(null)
          }}
        />
      )}

      {showPaymentModal && (
        <PaymentModal
          cart={cart}
          subtotal={subtotal}
          tax={tax}
          total={total}
          customers={customers}
          selectedCustomer={selectedCustomer}
          paymentMethod={paymentMethod}
          processing={processing}
          accountSlug={account.slug}
          serialNumbers={serialNumbers}
          amountReceived={amountReceived}
          orderNotes={orderNotes}
          consumptionType={consumptionType}
          tenantType={account.tenant_type}
          onSelectCustomer={setSelectedCustomer}
          onSelectPaymentMethod={setPaymentMethod}
          onSerialNumberChange={handleSerialNumberChange}
          onAmountReceivedChange={setAmountReceived}
          onOrderNotesChange={setOrderNotes}
          onConsumptionTypeChange={setConsumptionType}
          onSubmit={handleSubmitSale}
          onClose={() => setShowPaymentModal(false)}
          onCustomerCreated={handleCustomerCreated}
        />
      )}

      {showSaveCustomerModal && (
        <SaveCustomerModal
          accountSlug={account.slug}
          onSaved={(customer) => {
            handleCustomerCreated(customer)
            setShowSaveCustomerModal(false)
          }}
          onSkip={() => setShowSaveCustomerModal(false)}
        />
      )}
    </Layout>
  )
}

// ── Paginador estándar (flechas + números), spec 2026-07-21 ──
// Ventana de hasta 5 páginas alrededor de la actual, con ellipsis.

function PosPagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number
  totalPages: number
  onPageChange: (next: number) => void
}) {
  const pages: (number | '…')[] = []
  const windowSize = 5
  let start = Math.max(1, page - Math.floor(windowSize / 2))
  let end = Math.min(totalPages, start + windowSize - 1)
  start = Math.max(1, end - windowSize + 1)

  const pushRange = (from: number, to: number) => {
    for (let i = from; i <= to; i++) pages.push(i)
  }

  if (start > 1) {
    pages.push(1)
    if (start > 2) pages.push('…')
  }
  pushRange(start, end)
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push('…')
    pages.push(totalPages)
  }

  const btn =
    'min-w-9 h-9 px-2 rounded-lg text-sm font-medium border transition-colors'
  const inactive = 'bg-white text-[#202020] border-gray-300 hover:bg-gray-100'
  const active = 'bg-[#202020] text-white border-[#202020]'
  const disabled = 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed'

  return (
    <div className="mt-4 flex items-center justify-center gap-1 flex-wrap">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className={`${btn} ${page <= 1 ? disabled : inactive}`}
        aria-label="Página anterior"
      >
        ◀
      </button>
      {pages.map((p, idx) =>
        p === '…' ? (
          <span key={`e${idx}`} className="px-1 text-gray-400">
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={`${btn} ${p === page ? active : inactive}`}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className={`${btn} ${page >= totalPages ? disabled : inactive}`}
        aria-label="Página siguiente"
      >
        ▶
      </button>
    </div>
  )
}

// ── ProductCard ────────────────────────────────────

function ProductCard({ product, onClick }: { product: Product; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="bg-white p-3 rounded-lg shadow hover:shadow-md transition-shadow text-left active:bg-gray-50 min-h-[110px] flex flex-col"
    >
      {product.product_images.length > 0 ? (
        <div className="w-full h-20 mb-2 rounded bg-gray-100 overflow-hidden">
          <img
            src={product.product_images[0].url}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="w-full h-20 mb-2 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
          Sin imagen
        </div>
      )}

      <p className="font-medium text-[#202020] truncate text-sm leading-tight">
        {product.name}
      </p>
      {product.sku && (
        <p className="text-xs text-gray-400 truncate">{product.sku}</p>
      )}
      <p className="text-lg font-bold text-[#55ACEE] mt-auto pt-1">
        {formatCurrency(product.price)}
      </p>
      <div className="flex items-center gap-1 text-xs text-gray-400">
        <span>Stock: {product.stock_quantity}</span>
        {product.track_variants && product.variants.length > 0 && (
          <span className="bg-blue-100 text-blue-700 px-1.5 rounded text-[10px] font-medium">
            +{product.variants.length} var
          </span>
        )}
      </div>
    </button>
  )
}
